/* eslint-disable react/forbid-prop-types */
import React from 'react';
import { array, func, oneOfType, shape, string } from 'prop-types';
import * as R from 'ramda';
import { Box, Button } from '@mui/material';

import { styled } from '@mui/material/styles';
// Components
import { MDropdown } from 'common';

// Utils
import {
  SUPPLIER_AUTH_FILTERS,
  SUPPLIER_EXCHANGE_FILTER_OPTIONS,
  SUPPLIER_STATUS_FILTER_OPTIONS,
  SUPPLIER_TYPE_FILTER_OPTIONS,
} from '../../../../utils/constants';

const FilterButton = styled(Box, {
  shouldForwardProp: prop => !['showClear'].includes(prop),
})(({ showClear, theme }) => ({
  '&:first-of-type': {
    paddingTop: 0,
  },
  '&:last-of-type': {
    marginBottom: showClear ? theme.spacing(2) : 0,
  },
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  width: '100%',
}));

const FilterPopover = ({
  activeSupplierAuthTab,
  exchange,
  location,
  product,
  setFilters,
  state,
  status,
  supplier,
  supplierAuthorizations,
  type,
  viewBy,
}) => {
  const filterSelections = {
    exchange,
    location,
    product,
    state,
    status,
    supplier,
    type,
  };

  const showClear = Object.keys(filterSelections).some(
    key => filterSelections[key] !== SUPPLIER_AUTH_FILTERS.any
  );

  const clearFilter = () =>
    Object.keys(filterSelections).forEach(key => {
      if (filterSelections[key] !== SUPPLIER_AUTH_FILTERS.any) {
        setFilters({ name: key, value: SUPPLIER_AUTH_FILTERS.any });
      }
    });

  const anyOption = { label: 'Any', value: 'Any' };

  const getFilterOptions = (filterType, key = 'nameShort') => {
    if (R.isNil(supplierAuthorizations)) return null;
    const pathLens = R.lensPath([filterType, key]);

    return R.pipe(
      R.propOr(supplierAuthorizations, activeSupplierAuthTab),
      R.map(item => ({
        label: R.view(pathLens, item),
        value: R.view(pathLens, item),
      })),
      R.uniqBy(R.prop('label')),
      R.prepend(anyOption)
    )(supplierAuthorizations);
  };

  const locationOptions = getFilterOptions('location');
  const productOptions = getFilterOptions('product');
  const supplierOptions = getFilterOptions('supplier');
  const stateOptions = getFilterOptions('location', 'state');

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        px: 3,
        py: 2,
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        width: { xs: '100%', lg: 220 },
      }}
    >
      <FilterButton display="flex">
        <MDropdown
          anchorOrigin={{
            horizontal: 'center',
            vertical: 'top',
          }}
          id="exchange-select"
          items={SUPPLIER_EXCHANGE_FILTER_OPTIONS}
          label="Exchange Prop"
          name="exchange"
          onChange={setFilters}
          value={exchange}
        />
      </FilterButton>
      <FilterButton display="flex">
        <MDropdown
          anchorOrigin={{
            horizontal: 'center',
            vertical: 'top',
          }}
          id="type-select"
          items={SUPPLIER_TYPE_FILTER_OPTIONS}
          label="Type"
          name="type"
          onChange={setFilters}
          value={type}
        />
      </FilterButton>
      <FilterButton display="flex">
        <MDropdown
          anchorOrigin={{
            horizontal: 'center',
            vertical: 'top',
          }}
          id="status-select"
          items={SUPPLIER_STATUS_FILTER_OPTIONS}
          label="Status"
          name="status"
          onChange={setFilters}
          value={status}
        />
      </FilterButton>
      {supplierOptions?.length > 2 && viewBy !== 'supplier' && (
        <FilterButton display="flex">
          <MDropdown
            anchorOrigin={{
              horizontal: 'center',
              vertical: 'top',
            }}
            id="supplier-select"
            items={supplierOptions}
            label="Supplier"
            name="supplier"
            onChange={setFilters}
            value={supplier}
          />
        </FilterButton>
      )}
      {productOptions?.length > 2 && viewBy !== 'product' && (
        <FilterButton display="flex">
          <MDropdown
            anchorOrigin={{
              horizontal: 'center',
              vertical: 'top',
            }}
            id="product-select"
            items={productOptions}
            label="Product"
            name="product"
            onChange={setFilters}
            value={product}
          />
        </FilterButton>
      )}
      {locationOptions?.length > 2 && viewBy !== 'location' && (
        <FilterButton display="flex">
          <MDropdown
            anchorOrigin={{
              horizontal: 'center',
              vertical: 'top',
            }}
            id="location-select"
            items={locationOptions}
            label="Location"
            name="location"
            onChange={setFilters}
            value={location}
          />
        </FilterButton>
      )}
      {stateOptions?.length > 2 && (
        <FilterButton display="flex">
          <MDropdown
            anchorOrigin={{
              horizontal: 'center',
              vertical: 'top',
            }}
            id="state-select"
            items={stateOptions}
            label="State"
            name="state"
            onChange={setFilters}
            value={state}
          />
        </FilterButton>
      )}
      {showClear && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 0,
          }}
        >
          <Button aria-label="Clear" onClick={clearFilter} variant="text">
            Clear
          </Button>
        </Box>
      )}
    </Box>
  );
};

FilterPopover.propTypes = {
  activeSupplierAuthTab: string.isRequired,
  exchange: string,
  location: string,
  product: string,
  setFilters: func.isRequired,
  state: string,
  status: string,
  supplier: string,
  supplierAuthorizations: oneOfType([array, shape({})]),
  type: string,
  viewBy: string,
};

FilterPopover.defaultProps = {
  exchange: SUPPLIER_AUTH_FILTERS.any,
  location: SUPPLIER_AUTH_FILTERS.any,
  product: SUPPLIER_AUTH_FILTERS.any,
  state: SUPPLIER_AUTH_FILTERS.any,
  status: SUPPLIER_AUTH_FILTERS.any,
  supplier: SUPPLIER_AUTH_FILTERS.any,
  supplierAuthorizations: null,
  type: SUPPLIER_AUTH_FILTERS.any,
  viewBy: '',
};

export default FilterPopover;
